import { Chart, ChartData, Plugin } from 'chart.js';
import { FroelingHistoryPoint, PelletUsageBucket, SolarHistoryPoint } from '../version.service';
import { LinkWindow } from '../shared/deep-link.service';
import {
  alignedTimeTicks, chartPalette, circuitColor, exhaustColor, heatingCircuitColor, setpointColor, uiLocale, visibleTimeTick,
} from '../shared/chart.utils';
import { PHASE_COLORS, SparklineChartComponent } from '../sparkline-chart/sparkline-chart.component';
import { BoardLive, BoardRef, Tr, hcLabel, wxBinary, wxMeta } from './board-catalog';
import { BoardData, BucketMinMax, ExactStats, FieldStats, StatSource, XY, statSourceKey } from './board-data';
import { AxisWidths, alignAxis, tooltipTime } from './board-sync';
import { buildValveChart } from './valve-heatmap';
import {
  BOILER_STATES, BURNER_COLOR, STATE_CATEGORIES, STATE_CATEGORY_KEYS, STATE_CATEGORY_SHORT_KEYS, areaFill, burnerLegend, burnerShare,
  hydraulicsSeries, stateCategory,
} from '../shared/froeling.utils';

/**
 * Chart.js configs of the board's charts — on screen and, offscreen in the light theme, for the PDF.
 * They follow the dashboard's own charts: the set-point orange for every Soll, the heating circuits'
 * colours, the exhaust brown, a room's Ist blue with its valve as a teal area on a right 0–100 % axis,
 * the y-axis resolution of the source (Fröling 1 °C, rooms 0.1 °C, % 1, W 1), monotone lines and line
 * swatches in the legend. Every dataset has a linkKey: the legend selection (a slot's vis, a snapshot
 * link's "analyse.<chart id>.hide=…") is applied as the datasets' `hidden` ({@link applyVisibility}) and a
 * legend click is reported (BuildCtx.onLegend; none: the legend is not clickable). The x axis is the
 * board's window; a drag / pinch reports a new window.
 * Values that hold until the next one (KNX telegrams: valves, on / off channels, the circuit pump) are
 * drawn as steps from their own time on (Chart.js stepped 'before': a segment keeps the earlier
 * point's value) and summarised time-weighted. Every chart has a left and a right y axis whose widths
 * are those of the widest chart of the board (board-sync.ts alignAxis), so the plot areas line up.
 * A downsampled continuous line (bucket means) gets a faint band between its buckets' min and max
 * ({@link boardBandsPlugin}), so a long window still shows its short peaks; the stats below a chart come
 * from every raw point of the window ({@link seriesStat}).
 */

/** How a series is summarised below its chart over the window. */
export interface StatSpec {
  /** min · Ø · max | Σ | share of the time "on" | a valve: min · Ø · max and the time fully open / closed */
  kind: 'mmm' | 'sum' | 'on' | 'valve';
  unit: string;
  digits: number;
  /** Each value holds until the next point (a null ends it), the last one until the window's end: Ø and
   *  the "on" share are time-weighted ("on" always is). Only for the fallback from the chart data. */
  hold?: boolean;
  /** Where /api/history/stats has this series (every raw point of the window); without one the stats come
   *  from the chart's data (the pellets' sums, a KNX on / off channel). */
  src?: StatSrc;
  /** Without a source: the chart's data are bucket means ("≈"). */
  approx?: boolean;
}

/** A series' field in /api/history/stats. */
export interface StatSrc extends StatSource {
  /** Flattened key: "boilerTempC", "heatingCircuits.Heizkreis 01.flowTempActualC", "value" … */
  field: string;
  /** Charted as value × scale (−1: battery charge as +, 0.01: Pa → hPa); min and max swap below 0. */
  scale?: number;
  /** The chart leaves values ≤ 0 out (a Soll of 0 = none): stats with such values fall back to the chart's. */
  positive?: boolean;
}

/** One point of a series' min–max band: its bucket's min / max (null: none, a gap). */
export interface BandPt { x: number; lo: number | null; hi: number | null; }

export interface BuiltSeries {
  key: string;
  label: string;
  color: string;
  dash: number[];
  /** Hidden unless the legend (or the link) shows it. */
  hidden: boolean;
  stat: StatSpec | null;
  data: XY[];
}

export interface BuiltChart {
  type: 'line' | 'bar';
  data: ChartData<any>;
  options: any;
  /** Height the chart wants (CSS px; the valve heatmap: by its rows), else the board's default. */
  height?: number;
  series: BuiltSeries[];
  /** Points in the window (0: "no data"). */
  points: number;
}

export interface BuildCtx {
  dark: boolean;
  t: Tr;
  win: LinkWindow;
  /** Phone width: compact fonts and legend. */
  phone: boolean;
  /** Offscreen for the PDF: light, larger type, no interaction. */
  print: boolean;
  /** The legend selection: series toggled away from their default (linkKey → hidden). */
  vis: ReadonlyMap<string, boolean>;
  /** A legend click toggled a series: the chart's whole selection (null: the legend is not clickable). */
  onLegend: ((toggles: Map<string, boolean>) => void) | null;
  /** Crosshair group (board-sync.ts), null = none. */
  group: string | null;
  /** A drag / pinch selected this window (null: no zoom). */
  onWindow: ((win: LinkWindow) => void) | null;
  live: BoardLive;
  /** Fixed y-axis widths (the PDF: the widest of all its charts); else the crosshair group's. */
  axes?: AxisWidths | null;
}

const VALVE_FILL = { light: 'rgba(12,166,120,0.12)', dark: 'rgba(12,166,120,0.15)' };

const nfCache = new Map<string, Intl.NumberFormat>();
/** Locale number with fixed decimals ("18,4" in German). */
export function fmtNum(v: number, digits: number): string {
  const key = uiLocale() + digits;
  let f = nfCache.get(key);
  if (!f) { f = new Intl.NumberFormat(uiLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits }); nfCache.set(key, f); }
  return f.format(v);
}

function fmtW(w: number): string {
  return Math.abs(w) >= 1000 ? `${fmtNum(w / 1000, 1)} kW` : `${fmtNum(w, 0)} W`;
}

/** "Mi., 21.10. 14:35" (windows over ~a day) or "14:35". */
export function fmtTime(ts: number, win: LinkWindow): string {
  const long = win.to - win.from > 20 * 3600_000;
  return new Date(ts).toLocaleString(uiLocale(), long
    ? { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
    : { hour: '2-digit', minute: '2-digit' });
}

/** Collects datasets, their tooltip formatters and their stats series. */
export class Sets {
  readonly datasets: any[] = [];
  readonly fmts: ((v: number) => string)[] = [];
  readonly series: BuiltSeries[] = [];

  constructor(private readonly ctx: BuildCtx) {}

  /** band: the series' min–max band ({@link bandOf}; continuous lines only — no steps, on / off or Soll). */
  add(key: string, label: string, color: string, data: XY[], fmt: (v: number) => string,
      stat: StatSpec | null, opts: any = {}, band: BandPt[] | null = null): void {
    const width = this.ctx.print ? 2 : this.ctx.phone ? 1.5 : 2;
    this.datasets.push({
      linkKey: key,
      label,
      data,
      borderColor: color,
      backgroundColor: 'transparent',
      pointBackgroundColor: color,
      pointBorderColor: color,
      fill: false,
      borderWidth: width,
      pointRadius: 0,
      pointHoverRadius: 3,
      ...(band ? { band, bandAlpha: this.ctx.print ? 0.16 : this.ctx.dark ? 0.2 : 0.14 } : {}),
      ...opts,
    });
    this.fmts.push(fmt);
    this.series.push({ key, label, color, dash: opts.borderDash ?? [], hidden: !!opts.hidden, stat, data });
  }
}

const unitFmt = (digits: number, unit: string) => (v: number) => `${fmtNum(v, digits)}${unit ? ' ' + unit : ''}`;
const degC = unitFmt(1, '°C');
const pct0 = unitFmt(0, '%');

/** Build the chart of `ref` from its data, with the legend selection of ctx.vis. */
export function buildBoardChart(ref: BoardRef, data: BoardData | null, ctx: BuildCtx): BuiltChart {
  return applyVisibility(buildChart(ref, data, ctx), ctx.vis);
}

/**
 * The legend selection as the datasets' `hidden` (Chart.js creates fresh metas for new datasets, so a
 * rebuilt chart shows it); each dataset keeps its own default as `defaultHidden` (a legend click compares
 * against it, see {@link legendClick}).
 */
export function applyVisibility(built: BuiltChart, vis: ReadonlyMap<string, boolean>): BuiltChart {
  for (const ds of built.data.datasets as any[]) {
    ds.defaultHidden = !!ds.hidden;
    if (typeof ds.linkKey === 'string' && vis.has(ds.linkKey)) ds.hidden = vis.get(ds.linkKey);
  }
  return built;
}

/** Legend onClick: toggle the series (as Chart.js does), then report the chart's selection against the defaults. */
function legendClick(report: (toggles: Map<string, boolean>) => void) {
  return (_e: unknown, item: any, legend: any): void => {
    const chart: Chart = legend?.chart;
    const i = item?.datasetIndex;
    if (!chart || typeof i !== 'number') return;
    if (chart.isDatasetVisible(i)) chart.hide(i); else chart.show(i);
    const toggles = new Map<string, boolean>();
    chart.data.datasets.forEach((ds: any, k) => {
      if (typeof ds.linkKey !== 'string' || !ds.linkKey) return;
      const hidden = !chart.isDatasetVisible(k);
      if (hidden !== !!ds.defaultHidden) toggles.set(ds.linkKey, hidden);
    });
    report(toggles);
  };
}

function buildChart(ref: BoardRef, data: BoardData | null, ctx: BuildCtx): BuiltChart {
  // the valve overview: heatmap / one line per room (valve-heatmap.ts)
  if (ref.kind === 'valves') return buildValveChart(ref.floor, data?.kind === 'valves' ? data.rows : null, ctx);
  const sets = new Sets(ctx);
  const t = ctx.t;
  const dark = ctx.dark;
  let scales: Record<string, any> = {};
  /** A height of the chart's own (CSS px), else the board's default. */
  let height: number | undefined;
  const ax = axes(ctx);

  if (data?.kind === 'froeling' && (ref.kind === 'froeling' || ref.kind === 'hc')) {
    const pts = data.pts;
    const line = (acc: (p: FroelingHistoryPoint) => number | null | undefined): XY[] =>
      pts.map(p => { const v = acc(p); return { x: p.ts, y: typeof v === 'number' && isFinite(v) ? v : null }; });
    const tempStat: StatSpec = { kind: 'mmm', unit: '°C', digits: 1 };
    const pctStat: StatSpec = { kind: 'mmm', unit: '%', digits: 0 };
    // stats of a field over the window's raw points; band: its buckets' min / max
    const fs = (spec: StatSpec, field: string, more: Partial<StatSrc> = {}): StatSpec =>
      ({ ...spec, src: { series: 'froeling', field, ...more } });
    const band = (field: string) => bandOf(pts, field);
    // the burner: share of the time it burned (a minute 0 / 1, a bucket its share) — exact stats from the archived
    // burnerOn when every point of the window has it, else from the chart's data
    const burnerFmt = (v: number): string => v >= 0.995 ? t('BOARD_ON') : v <= 0.005 ? t('BOARD_OFF') : `${t('BOARD_ON')} ${fmtNum(v * 100, 0)} %`;
    const burnerStat: StatSpec = pts.length && pts.every(p => typeof p.burnerOn === 'number')
      ? fs({ kind: 'on', unit: '', digits: 0 }, 'burnerOn')
      : { kind: 'on', unit: '', digits: 0, hold: true, approx: pts.some(p => (p as BucketMinMax)._min) };
    scales = { y: ax.froelingTemp };
    if (ref.kind === 'hc') {
      const hc = (p: FroelingHistoryPoint) => p.heatingCircuits?.find(h => h.name === ref.name);
      const hk = (field: string) => `heatingCircuits.${ref.name}.${field}`;
      sets.add('ist', t('BOARD_DS_FLOW_IST'), circuitColor(dark), line(p => hc(p)?.flowTempActualC), degC,
        fs(tempStat, hk('flowTempActualC')), {}, band(hk('flowTempActualC')));
      const soll = line(p => { const v = hc(p)?.flowTempTargetC ?? 0; return v > 0 && active(p, hk('flowTempTargetC')) ? v : null; });
      if (soll.some(d => d.y != null)) {
        sets.add('soll', t('BOARD_DS_FLOW_SOLL'), setpointColor(dark), soll, degC, fs(tempStat, hk('flowTempTargetC'), { positive: true }),
          { borderDash: [5, 4], borderWidth: 1.5 });
      }
      const pumpColor = dark ? '#adb5bd' : '#6c757d';
      sets.add('pump', t('HEIZUNG_PUMPE'), pumpColor,
        line(p => { const s = hc(p)?.pumpState; return s == null ? null : onOff(s); }),
        v => v >= 0.5 ? t('BOARD_ON') : t('BOARD_OFF'), fs({ kind: 'on', unit: '', digits: 0 }, hk('pumpState')),
        { yAxisID: 'y1', stepped: 'before', fill: 'origin', borderWidth: 1.5,
          backgroundColor: dark ? 'rgba(173,181,189,0.12)' : 'rgba(108,117,125,0.1)' });
      scales['y1'] = ax.onOff;
    } else {
      switch (ref.part) {
        case 'boiler':
          sets.add('boilerTempC', t('HEIZUNG_KESSEL'), '#dc3545', line(p => p.boilerTempC), degC,
            fs(tempStat, 'boilerTempC'), {}, band('boilerTempC'));
          sets.add('exhaustTempC', t('HEIZUNG_ABGAS'), exhaustColor(dark), line(p => p.exhaustTempC), degC,
            fs(tempStat, 'exhaustTempC'), {}, band('exhaustTempC'));
          break;
        case 'buffer':
          sets.add('bufferTopTempC', t('BOARD_DS_TOP'), '#dc3545', line(p => p.bufferTopTempC), degC,
            fs(tempStat, 'bufferTopTempC'), {}, band('bufferTopTempC'));
          sets.add('bufferBottomTempC', t('BOARD_DS_BOTTOM'), '#0d6efd', line(p => p.bufferBottomTempC), degC,
            fs(tempStat, 'bufferBottomTempC'), {}, band('bufferBottomTempC'));
          sets.add('bufferChargePct', t('BOARD_DS_CHARGE'), '#ffc107', line(p => p.bufferChargePct), pct0, fs(pctStat, 'bufferChargePct'),
            { yAxisID: 'y1', fill: 'origin', backgroundColor: 'rgba(255,193,7,0.15)', borderWidth: 1.5 }, band('bufferChargePct'));
          scales['y1'] = ax.pct;
          break;
        case 'water':
          sets.add('hotWaterTempC', t('HEIZUNG_WARMWASSER'), '#0dcaf0', line(p => p.hotWaterTempC), degC,
            fs(tempStat, 'hotWaterTempC'), {}, band('hotWaterTempC'));
          break;
        case 'solarThermal':
          sets.add('solarThermalTempC', t('BOARD_DS_COLLECTOR'), '#ffc107', line(p => p.solarThermalTempC), degC,
            fs(tempStat, 'solarThermalTempC'), {}, band('solarThermalTempC'));
          break;
        case 'outdoor':
          sets.add('outdoorTempC', t('HEIZUNG_AUSSEN'), '#6c757d', line(p => p.outdoorTempC), degC,
            fs(tempStat, 'outdoorTempC'), {}, band('outdoorTempC'));
          break;
        case 'pump':
          sets.add('circulationPumpPct', t('BOARD_DS_PUMP_SPEED'), '#0d6efd', line(p => p.circulationPumpPct ?? 0), pct0,
            fs(pctStat, 'circulationPumpPct'), { fill: 'origin', backgroundColor: 'rgba(13,110,253,0.15)' }, band('circulationPumpPct'));
          scales = { y: ax.pctLeft };
          break;
        case 'hydraulics': {
          // where the heat goes: temperatures on °C; below them in a strip of their own (a stacked 0–100 % axis, so
          // they never cover the temperatures) the buffer / DHW pump; the burner shaded over the full height while it
          // burns (a bucket: as high as the share of its time it burned)
          for (const s of hydraulicsSeries(pts, t, dark, n => hcLabel(n, t))) {
            if (s.key === 'burner') {
              sets.add('burner', s.label, s.color, line(s.value), burnerFmt, burnerStat,
                { yAxisID: 'y2', stepped: 'before', tension: 0, fill: 'origin', borderWidth: 0.75, order: 3,
                  backgroundColor: areaFill(s.color, dark, true), ...burnerLegend(s.color, dark) });
            } else if (s.area) {
              sets.add(s.key, s.label, s.color, line(s.value), pct0, fs(pctStat, s.field!),
                { yAxisID: 'yp', stepped: 'before', tension: 0, fill: 'origin', borderWidth: 1.25, borderDash: s.dash, order: 2,
                  backgroundColor: areaFill(s.color, dark, false) });
            } else if (s.soll) {
              const field = s.field!;
              sets.add(s.key, s.label, s.color, line(p => active(p, field) ? s.value(p) : null), degC,
                fs(tempStat, field, { positive: true }), { yAxisID: 'y', borderDash: s.dash, borderWidth: 1.5 });
            } else {
              // yAxisID explicit: Chart.js' default would be the first vertical scale, the pump strip
              sets.add(s.key, s.label, s.color, line(s.value), degC, fs(tempStat, s.field!),
                { yAxisID: 'y', ...(s.dash ? { borderDash: s.dash } : {}) }, band(s.field!));
            }
          }
          // of stacked axes the first one is drawn at the bottom: the pump strip below the temperatures. No `offset`
          // (it pads half a tick step at each end, and autoSkip then drops the 100 % label of a short strip): a fixed
          // margin instead, both labels kept — above 100 % a gap, so its label stays clear of the temperatures' lowest one
          scales = {
            yp: { ...ax.pctLeft, suggestedMax: undefined, min: -8, max: 135, stack: 'hyd', stackWeight: 2, afterFit: alignAxis,
                  ticks: { ...ax.pctLeft.ticks, autoSkip: false },
                  afterBuildTicks: (sc: any) => { sc.ticks = [0, 100].map(value => ({ value })); } },
            y: { ...scales['y'], stack: 'hyd', stackWeight: 4 },
            y2: { min: 0, max: 1, display: false },
          };
          // a phone: the 11-entry legend takes much of the board's 220 px
          if (ctx.phone) height = 330;
          break;
        }
        case 'pumps': {
          // every pump on 0–100 %: steps (a pump's output holds until the next minute). Pumps sit at 0 or 100 % most
          // of the time, so each one is drawn in a lane of its own (3 % further from 0 / 100 toward the middle per
          // pump): they never lie on top of each other; the tooltip and the stats show the real value
          const step = { stepped: 'before', tension: 0, borderWidth: 1.5 };
          let lane = 0;
          const laned = (v: number | null | undefined, k: number): number | null =>
            typeof v === 'number' && isFinite(v) ? (v >= 50 ? v - 3 * k : v + 3 * k) : null;
          const unlaned = (v: number, k: number): number => v >= 50 ? v + 3 * k : v - 3 * k;
          const pump = (key: string, label: string, color: string, field: string, acc: (p: FroelingHistoryPoint) => number | null | undefined,
                        opts: any = {}) => {
            if (!pts.some(p => num(acc(p)) != null)) return;
            const k = lane++;
            sets.add(key, label, color, line(p => laned(acc(p), k)), v => pct0(unlaned(v, k)), fs(pctStat, field), { ...step, ...opts });
            sets.series[sets.series.length - 1].data = line(acc);   // the stats' fallback: the real values
          };
          pump('bufferPumpPct', t('HYD_BUFFER_PUMP'), dark ? '#8c68cd' : '#6f42c1', 'bufferPumpPct', p => p.bufferPumpPct,
            { fill: 'origin', backgroundColor: areaFill(dark ? '#8c68cd' : '#6f42c1', dark, false) });
          pump('dhwPumpPct', t('HYD_DHW_PUMP'), dark ? '#20c997' : '#12b886', 'dhwPumpPct', p => p.dhwPumpPct, { borderDash: [4, 3] });
          pump('circulationPumpPct', t('BOARD_DS_CIRC_PUMP'), '#0d6efd', 'circulationPumpPct', p => p.circulationPumpPct, { borderDash: [2, 2] });
          pump('solarThermalPumpPct', t('BOARD_DS_SOLAR_PUMP'), '#f59f00', 'solarThermalPumpPct', p => p.solarThermalPumpPct);
          // the circuit pumps (on / off) in greys, each with its own dash — away from the green / purple of the DHW
          // and buffer pumps
          const hcGreys = dark ? ['#adb5bd', '#dee2e6', '#868e96', '#f8f9fa'] : ['#868e96', '#495057', '#adb5bd', '#212529'];
          const hcDashes: number[][] = [[6, 3], [10, 3, 2, 3], [3, 3], [12, 4]];
          const names = pts.find(p => p.heatingCircuits?.length)?.heatingCircuits.map(h => h.name) ?? [];
          names.forEach((name, i) => {
            const field = `heatingCircuits.${name}.pumpState`;
            const k = lane++;
            const data = line(p => { const s = p.heatingCircuits?.find(h => h.name === name)?.pumpState; return s == null ? null : laned(onOff(s) * 100, k); });
            sets.add(`hcPump:${name}`, t('BOARD_DS_HC_PUMP', { name: hcLabel(name, t) }), hcGreys[i % hcGreys.length], data,
              v => v >= 50 ? t('BOARD_ON') : t('BOARD_OFF'), fs({ kind: 'on', unit: '', digits: 0 }, field),
              { ...step, borderDash: hcDashes[i % hcDashes.length], borderWidth: 1.5 });
            sets.series[sets.series.length - 1].data =
              line(p => { const s = p.heatingCircuits?.find(h => h.name === name)?.pumpState; return s == null ? null : onOff(s) * 100; });
          });
          scales = { y: { ...ax.pctLeft, suggestedMax: undefined, min: -2, max: 102,
                          afterBuildTicks: (sc: any) => { sc.ticks = [0, 25, 50, 75, 100].map(value => ({ value })); } } };
          break;
        }
        case 'solarCircuit': {
          sets.add('solarThermalTempC', t('BOARD_DS_COLLECTOR'), '#fab005', line(p => p.solarThermalTempC), degC,
            fs(tempStat, 'solarThermalTempC'), {}, band('solarThermalTempC'));
          const temp = (key: string, label: string, color: string, acc: (p: FroelingHistoryPoint) => number | null | undefined, opts: any = {}) => {
            const data = line(acc);
            if (data.some(d => d.y != null)) sets.add(key, label, color, data, degC, fs(tempStat, key), opts, band(key));
          };
          temp('collectorFlowTempC', t('BOARD_DS_COLLECTOR_FLOW'), dark ? '#f06595' : '#c2255c', p => p.collectorFlowTempC);
          temp('collectorReturnTempC', t('BOARD_DS_COLLECTOR_RETURN'), dark ? '#91a7ff' : '#5c7cfa', p => p.collectorReturnTempC);
          temp('solarBufferTopTempC', t('BOARD_DS_SOLAR_BUF_TOP'), dark ? '#6ea8fe' : '#0d6efd', p => p.solarBufferTopTempC, { borderDash: [4, 3] });
          temp('solarBufferBottomTempC', t('BOARD_DS_SOLAR_BUF_BOTTOM'), dark ? '#9ec5fe' : '#6ea8fe', p => p.solarBufferBottomTempC, { borderDash: [2, 3] });
          const pumpData = line(p => p.solarThermalPumpPct);
          if (pumpData.some(d => d.y != null)) {
            sets.add('solarThermalPumpPct', t('BOARD_DS_SOLAR_PUMP'), '#868e96', pumpData, pct0, fs(pctStat, 'solarThermalPumpPct'),
              { yAxisID: 'y1', stepped: 'before', tension: 0, fill: 'origin', borderWidth: 1, backgroundColor: areaFill('#868e96', dark, false) });
            scales['y1'] = ax.pct;
          }
          break;
        }
        case 'combustion': {
          // yAxisID explicit: Chart.js' default would be the first vertical scale, the O₂ strip
          sets.add('boilerTempC', t('HEIZUNG_KESSEL'), '#dc3545', line(p => p.boilerTempC), degC, fs(tempStat, 'boilerTempC'), { yAxisID: 'y' },
            band('boilerTempC'));
          sets.add('exhaustTempC', t('HEIZUNG_ABGAS'), exhaustColor(dark), line(p => p.exhaustTempC), degC, fs(tempStat, 'exhaustTempC'),
            { yAxisID: 'y' }, band('exhaustTempC'));
          // residual O₂ (≈ 2–12 %, at most the air's 21 %) in a strip of its own below the temperatures (0–25 %): on
          // the draft fan's 0–100 % it would be a flat line at the bottom; the fan on the right axis, full height
          const o2 = line(p => p.oxygenPct), fan = line(p => p.draftFanPct);
          const hasO2 = o2.some(d => d.y != null);
          if (hasO2) {
            sets.add('oxygenPct', t('BOARD_DS_O2'), dark ? '#38d9a9' : '#0ca678', o2, unitFmt(1, '%'), fs({ kind: 'mmm', unit: '%', digits: 1 }, 'oxygenPct'),
              { yAxisID: 'yo' }, band('oxygenPct'));
          }
          if (fan.some(d => d.y != null)) {
            sets.add('draftFanPct', t('BOARD_DS_DRAFT_FAN'), dark ? '#9775fa' : '#7048e8', fan, pct0, fs(pctStat, 'draftFanPct'),
              { yAxisID: 'y1', borderDash: [4, 3], stepped: 'before', tension: 0 });
          }
          scales['y1'] = ax.pct;
          if (hasO2) {
            // of stacked axes the first one is drawn at the bottom
            scales = {
              yo: { ...ax.pctLeft, suggestedMax: undefined, min: 0, max: 28, stack: 'comb', stackWeight: 1.8, afterFit: alignAxis,
                    ticks: { ...ax.pctLeft.ticks, autoSkip: false },
                    afterBuildTicks: (sc: any) => { sc.ticks = [0, 10, 20].map(value => ({ value })); } },
              y: { ...scales['y'], stack: 'comb', stackWeight: 4 },
              y1: scales['y1'],
            };
          }
          break;
        }
        case 'state': {
          // the state as its category (a step per minute; a downsampled bucket: its most frequent state); the
          // code rides along in the 4th decimal, so the tooltip names the state itself
          const cats = STATE_CATEGORIES;
          const data = line(p => {
            const c = stateCategory(p.boilerStateCode);
            return c == null ? null : cats.indexOf(c) + Math.round(p.boilerStateCode!) / 10_000;
          });
          const stateName = (v: number): string => {
            const idx = Math.round(v), code = Math.round((v - Math.floor(v + 1e-9)) * 10_000);
            const cat = t(STATE_CATEGORY_KEYS[cats[Math.max(0, Math.min(cats.length - 1, idx))]]);
            const key = BOILER_STATES[String(code)];
            if (!key) return `${cat} · ${code}`;
            const s = t(key);
            // "Heizen", not "Heizen (Heizen)": the category only where it adds something
            return s.trim().toLowerCase() === cat.trim().toLowerCase() ? s : `${s} (${cat})`;
          };
          sets.add('state', t('BOARD_DS_STATE'), dark ? '#dee2e6' : '#343a40', data, stateName, null,
            { stepped: 'before', tension: 0, borderWidth: 1.5 });
          const burnerColor = BURNER_COLOR(dark);
          sets.add('burner', t('HYD_BURNER'), burnerColor, line(p => burnerShare(p)), burnerFmt, burnerStat,
            { yAxisID: 'y1', stepped: 'before', tension: 0, fill: 'origin', borderWidth: 1, backgroundColor: areaFill(burnerColor, dark, true),
              ...burnerLegend(burnerColor, dark) });
          const { textColor, gridColor } = palette(ctx);
          scales = {
            y: {
              min: -0.5, max: cats.length - 0.5, grid: { color: gridColor },
              afterBuildTicks: (s: any) => { s.ticks = cats.map((_, i) => ({ value: i })); },
              // short names: every chart of the board gets this axis' width (alignAxis); the tooltip names the state
              ticks: { color: textColor, font: { size: fontSize(ctx) }, autoSkip: false, padding: 2,
                       callback: (v: any) => t(STATE_CATEGORY_SHORT_KEYS[cats[Math.round(Number(v))]] ?? '') },
            },
            y1: ax.onOff,
          };
          break;
        }
        case 'all': {
          // as the 📈 heating modal
          const f = (field: string) => fs(tempStat, field);
          sets.add('boilerTempC', t('HEIZUNG_KESSEL'), '#dc3545', line(p => p.boilerTempC), degC, f('boilerTempC'), {}, band('boilerTempC'));
          sets.add('bufferTopTempC', t('BOARD_DS_BUFFER_TOP'), '#0d6efd', line(p => p.bufferTopTempC), degC, f('bufferTopTempC'), {},
            band('bufferTopTempC'));
          sets.add('bufferBottomTempC', t('BOARD_DS_BUFFER_BOTTOM'), '#6ea8fe', line(p => p.bufferBottomTempC), degC, f('bufferBottomTempC'),
            { borderDash: [4, 3] }, band('bufferBottomTempC'));
          sets.add('hotWaterTempC', t('HEIZUNG_WARMWASSER'), '#0dcaf0', line(p => p.hotWaterTempC), degC, f('hotWaterTempC'), {},
            band('hotWaterTempC'));
          sets.add('outdoorTempC', t('HEIZUNG_AUSSEN'), '#6c757d', line(p => p.outdoorTempC), degC, f('outdoorTempC'),
            { borderDash: [6, 4] }, band('outdoorTempC'));
          if (pts.some(p => p.solarThermalTempC > 0)) {
            sets.add('solarThermalTempC', t('HEIZUNG_SOLAR'), '#ffc107', line(p => p.solarThermalTempC), degC, f('solarThermalTempC'), {},
              band('solarThermalTempC'));
          }
          if (pts.some(p => p.exhaustTempC > 0)) {
            sets.add('exhaustTempC', t('HEIZUNG_ABGAS'), exhaustColor(dark), line(p => p.exhaustTempC), degC, f('exhaustTempC'), {},
              band('exhaustTempC'));
          }
          const names = pts.find(p => p.heatingCircuits?.length)?.heatingCircuits.map(h => h.name) ?? [];
          const sollDashes: number[][] = [[5, 4], [2, 3], [8, 3, 2, 3], [1, 3]];
          names.forEach((name, i) => {
            const hc = (p: FroelingHistoryPoint) => p.heatingCircuits?.find(h => h.name === name);
            const label = hcLabel(name, t);   // "Heating Circuit 01"; the linkKeys keep the raw name
            const hk = (field: string) => `heatingCircuits.${name}.${field}`;
            sets.add(`hc:${name}`, label, heatingCircuitColor(i, dark), line(p => hc(p)?.flowTempActualC), degC, f(hk('flowTempActualC')), {},
              band(hk('flowTempActualC')));
            const soll = line(p => { const v = hc(p)?.flowTempTargetC ?? 0; return v > 0 && active(p, hk('flowTempTargetC')) ? v : null; });
            if (soll.some(d => d.y != null)) {
              sets.add(`hcSoll:${name}`, t('HEIZUNG_HK_SOLL_LINE', { name: label }), setpointColor(dark), soll, degC,
                fs(tempStat, hk('flowTempTargetC'), { positive: true }), { borderDash: sollDashes[i % sollDashes.length], borderWidth: 1.5 });
            }
          });
          sets.add('bufferChargePct', t('BOARD_DS_BUFFER_PCT'), '#a855f7', line(p => p.bufferChargePct), pct0, fs(pctStat, 'bufferChargePct'),
            { yAxisID: 'y1', borderDash: [4, 3] }, band('bufferChargePct'));
          scales['y1'] = ax.pct;
          break;
        }
      }
    }
  } else if (data?.kind === 'thermo' && ref.kind === 'thermo') {
    const pts = data.pts;
    const roomStat: StatSpec = { kind: 'mmm', unit: '°C', digits: 1 };
    const room = (field: string): StatSrc => ({ series: 'thermo-' + ref.deviceId, field });
    sets.add('ist', t('TEMP_ACTUAL'), '#0d6efd', pts.map(p => ({ x: p.ts, y: num(p.tempC) })), degC,
      { ...roomStat, src: room('tempC') }, {}, bandOf(pts, 'tempC'));
    const soll = pts.map(p => ({ x: p.ts, y: num(p.setpointC) }));
    if (soll.some(d => d.y != null)) {
      // set-point changes are steps: no smoothing, dashed in the Soll colour
      sets.add('soll', t('TEMP_SETPOINT'), setpointColor(dark), soll, degC, { ...roomStat, src: room('setpointC') },
        { stepped: 'before', tension: 0, spanGaps: true, borderDash: [5, 4] });
    }
    scales = { y: ax.roomTemp(soll.some(d => d.y != null)) };
    if (data.valve) {
      // a position holds from its telegram on ('before': a step keeps the earlier value); a PWM duty is a line
      // stats: the KNX series time-weighted (with the time fully open / closed), else the room points' own values
      const valveSrc: StatSrc = data.valveSeries ? { series: data.valveSeries, field: 'value', agg: 'time' } : room('valvePct');
      sets.add('valve', t('KNX_VALVE'), SparklineChartComponent.VALVE_COLOR, data.valve, pct0,
        { kind: 'valve', unit: '%', digits: 0, hold: data.valveStepped, src: valveSrc }, {
          yAxisID: 'y1', fill: 'origin', backgroundColor: dark ? VALVE_FILL.dark : VALVE_FILL.light,
          stepped: data.valveStepped ? 'before' : false, tension: 0, borderWidth: 1.5, order: 1,
        });
    }
    const hum = pts.map(p => ({ x: p.ts, y: num(p.humidity) }));
    if (hum.some(d => d.y != null)) {
      sets.add('humidity', t('KNX_WX_HUMIDITY'), '#0dcaf0', hum, pct0, { kind: 'mmm', unit: '%', digits: 0, src: room('humidity') },
        { yAxisID: 'y1', hidden: true, borderDash: [2, 3] }, bandOf(pts, 'humidity'));
    }
    if (data.valve || hum.some(d => d.y != null)) scales['y1'] = ax.pct;
  } else if (data?.kind === 'solar' && ref.kind === 'solar') {
    const pts = data.pts;
    const line = (acc: (p: SolarHistoryPoint) => number | null | undefined): XY[] =>
      pts.map(p => ({ x: p.ts, y: num(acc(p)) }));
    const wStat: StatSpec = { kind: 'mmm', unit: 'W', digits: 0 };
    const pctStat: StatSpec = { kind: 'mmm', unit: '%', digits: 0 };
    const fs = (spec: StatSpec, field: string, scale?: number): StatSpec => ({ ...spec, src: { series: 'solar', field, scale } });
    const band = (field: string, scale?: number) => bandOf(pts, field, scale);
    scales = { y: ax.watt };
    switch (ref.part) {
      case 'pvYield':
        sets.add('pv', t('CHART_ERTRAG'), '#ffc107', line(p => p.pvYieldW), fmtW, fs(wStat, 'pvYieldW'), {}, band('pvYieldW'));
        break;
      case 'battery':
        sets.add('power', t('BOARD_DS_BATTERY_POWER'), '#198754', line(p => -p.batteryDischargeW), fmtW, fs(wStat, 'batteryDischargeW', -1), {},
          band('batteryDischargeW', -1));
        sets.add('soc', t('AKKU_SOC'), '#a855f7', line(p => p.batteryPercent), pct0, fs(pctStat, 'batteryPercent'),
          { yAxisID: 'y1', borderDash: [4, 3] }, band('batteryPercent'));
        scales['y1'] = ax.pct;
        break;
      case 'grid':
        sets.add('grid', t('CHART_NETZ'), '#0dcaf0', line(p => p.gridConsumptionW), fmtW, fs(wStat, 'gridConsumptionW'), {},
          band('gridConsumptionW'));
        break;
      case 'consumption':
        sets.add('total', t('VERBRAUCH'), '#0d6efd', line(p => p.totalConsumptionW), fmtW, fs(wStat, 'totalConsumptionW'), {},
          band('totalConsumptionW'));
        if (pts.some(p => (p.consumptionL1W ?? 0) > 0 || (p.consumptionL2W ?? 0) > 0 || (p.consumptionL3W ?? 0) > 0)) {
          (['L1', 'L2', 'L3'] as const).forEach((ph, i) => sets.add(ph.toLowerCase(), ph, PHASE_COLORS[i],
            line(p => (p as any)[`consumption${ph}W`] ?? 0), fmtW, fs(wStat, `consumption${ph}W`), { hidden: true },
            band(`consumption${ph}W`)));
        }
        break;
      case 'all':
        // as the 📈 energy modal
        sets.add('pv', t('CHART_ERTRAG'), '#f59e0b', line(p => p.pvYieldW), fmtW, fs(wStat, 'pvYieldW'), {}, band('pvYieldW'));
        sets.add('battery', t('AKKU'), '#22c55e', line(p => p.batteryDischargeW), fmtW, fs(wStat, 'batteryDischargeW'), {},
          band('batteryDischargeW'));
        sets.add('grid', t('CHART_NETZ'), '#ef4444', line(p => p.gridConsumptionW), fmtW, fs(wStat, 'gridConsumptionW'), {},
          band('gridConsumptionW'));
        sets.add('total', t('VERBRAUCH'), '#0d6efd', line(p => p.totalConsumptionW), fmtW, fs(wStat, 'totalConsumptionW'), {},
          band('totalConsumptionW'));
        sets.add('soc', t('AKKU_SOC'), '#a855f7', line(p => p.batteryPercent), pct0, fs(pctStat, 'batteryPercent'),
          { yAxisID: 'y1', borderDash: [4, 3] }, band('batteryPercent'));
        scales['y1'] = ax.pct;
        break;
    }
  } else if (data?.kind === 'zisterne' && ref.kind === 'zisterne') {
    sets.add('level', t('BOARD_DS_LEVEL'), '#0d6efd', data.pts.map(p => ({ x: p.ts, y: num(p.levelPct) })), pct0,
      { kind: 'mmm', unit: '%', digits: 0, src: { series: 'zisterne', field: 'levelPct' } },
      { fill: 'origin', backgroundColor: 'rgba(13,110,253,0.15)' }, bandOf(data.pts, 'levelPct'));
    scales = { y: ax.pctLeft };
  } else if (data?.kind === 'wx' && ref.kind === 'wx') {
    const ch = ctx.live.knxWeather?.channels.find(c => c.series === ref.series) ?? null;
    const meta = wxMeta(ch);
    const color = dark ? meta.dark : meta.light;
    if (wxBinary(ch)) {
      const pts: XY[] = data.pts.map(p => ({ x: p.ts, y: p.value }));
      if (pts.length) pts.push({ x: Math.min(ctx.win.to, Date.now()), y: pts[pts.length - 1].y });   // the state holds
      // a telegram's state holds from its time on ('before': a step keeps the earlier value)
      // the share "on" from the telegrams themselves (each holds until the next): such a channel is sent on
      // change, the endpoint's time weighting holds a value only for FRESH_MS (the cyclic valves)
      sets.add('value', t(meta.label), color, pts, v => v >= 0.5 ? t('BOARD_ON') : t('BOARD_OFF'),
        { kind: 'on', unit: '', digits: 0, hold: true, approx: data.pts.some(p => p.lo != null) },
        { stepped: 'before', tension: 0, fill: 'origin', backgroundColor: color + '33' });
      scales = { y: ax.onOffLeft };
    } else {
      let unit = ch?.unit ?? meta.unit;
      let scale = 1;
      // pressure in Pa (DPT 9.006) reads better in hPa
      if (ch?.quantity === 'pressure' && (unit === 'Pa' || data.pts.some(p => Math.abs(p.value) > 2000))) { unit = 'hPa'; scale = 0.01; }
      const digits = meta.digits;
      // KNX values hold until the next telegram: Ø time-weighted (a value sent on change is not over-counted)
      const band: BandPt[] = data.pts.map(p => p.lo != null && p.hi != null
        ? { x: p.ts, lo: Math.min(p.lo, p.hi) * scale, hi: Math.max(p.lo, p.hi) * scale } : { x: p.ts, lo: null, hi: null });
      sets.add('value', t(meta.label), color, data.pts.map(p => ({ x: p.ts, y: p.value * scale })), unitFmt(digits, unit),
        { kind: 'mmm', unit, digits, hold: true, src: { series: ref.series, field: 'value', agg: 'time', scale } }, {},
        band.some(b => b.lo != null) ? band : null);
      scales = { y: ax.generic(digits === 0 ? 1 : Math.pow(10, -digits), unit, digits) };
    }
  } else if (data?.kind === 'pellets' && ref.kind === 'pellets') {
    return pelletChart(data.bars, data.line, data.short, ctx);
  }

  const points = sets.series.reduce((n, s) => n + s.data.filter(d => d.y != null && d.x >= ctx.win.from && d.x <= ctx.win.to).length, 0);
  return {
    type: 'line',
    data: { datasets: sets.datasets },
    options: baseOptions(ctx, scales, sets.fmts),
    ...(height ? { height } : {}),
    series: sets.series,
    points,
  };
}

function num(v: unknown): number | null {
  return typeof v === 'number' && isFinite(v) ? v : null;
}

/** A bucket without a value ≤ 0 of `field` (a Soll of 0 = none: a bucket mean with some would be a false low). */
function active(p: unknown, field: string): boolean {
  const lo = (p as BucketMinMax)?._min?.[field];
  return !(typeof lo === 'number' && lo <= 0);
}

function onOff(state: string): number {
  const s = state.toLowerCase().trim();
  return s === 'ein' || s === 'on' || s === 'an' || s === '1' || s === 'true' ? 1 : 0;
}

/** Pellets: bars = kg per hour (short windows) / day, line = burn rate kg/h (10-min mean / mean while burning). */
function pelletChart(bars: PelletUsageBucket[], line: PelletUsageBucket[], short: boolean, ctx: BuildCtx): BuiltChart {
  const t = ctx.t, dark = ctx.dark;
  const bucketMs = short ? 3600_000 : 86_400_000;
  const lineBucketMs = short ? 600_000 : 86_400_000;
  const barColor = dark ? '#a1887f' : '#795548';
  const barFaded = dark ? 'rgba(161,136,127,0.45)' : 'rgba(121,85,72,0.4)';
  const lineColor = dark ? '#ff6b6b' : '#dc3545';
  const rateOf = (b: PelletUsageBucket) => short ? b.rateKgH : b.burnRateKgH;
  const barData: XY[] = bars.map(b => ({ x: b.ts + bucketMs / 2, y: num(b.kg) }));
  const lineData: XY[] = line.map(b => ({ x: b.ts + lineBucketMs / 2, y: num(rateOf(b)) }));
  const kgLabel = t(short ? 'HEIZUNG_PELLET_KG_STUNDE' : 'HEIZUNG_PELLET_KG_TAG');
  const rateLabel = t(short ? 'HEIZUNG_PELLET_RATE' : 'HEIZUNG_PELLET_RATE_BURNING');
  const datasets: any[] = [
    {
      type: 'bar', linkKey: 'kg', label: kgLabel, data: barData,
      backgroundColor: bars.map(b => b.provisional ? barFaded : barColor), borderColor: barColor,
      borderRadius: 3, barPercentage: 0.9, categoryPercentage: 1, yAxisID: 'y', order: 2,
    },
    {
      type: 'line', linkKey: 'rate', label: rateLabel, data: lineData,
      borderColor: lineColor, backgroundColor: lineColor, pointBackgroundColor: lineColor, pointBorderColor: lineColor,
      borderWidth: 2, pointRadius: short ? 0 : 2.5, pointHoverRadius: 4, spanGaps: false, yAxisID: 'y1', order: 1,
    },
  ];
  const kg = unitFmt(1, 'kg');
  const kgh = unitFmt(1, 'kg/h');
  const series: BuiltSeries[] = [
    { key: 'kg', label: kgLabel, color: barColor, dash: [], hidden: false, stat: { kind: 'sum', unit: 'kg', digits: 1 }, data: barData },
    { key: 'rate', label: rateLabel, color: lineColor, dash: [], hidden: false, stat: { kind: 'mmm', unit: 'kg/h', digits: 1 }, data: lineData },
  ];
  const ax = axes(ctx);
  // bars (kg in a bucket) and line (kg/h) share one scale on short windows: the same quantity
  const sharedMax = (scale: any) => {
    if (!short) return;
    let m = 7;
    for (const d of [...barData, ...lineData]) if (d.y != null && d.x >= ctx.win.from && d.x <= ctx.win.to) m = Math.max(m, d.y);
    scale.min = 0;
    scale.max = Math.ceil(m);
  };
  const scales = {
    y: { ...ax.generic(0.1, 'kg', 0), position: 'left', beginAtZero: true, afterDataLimits: sharedMax },
    y1: { ...ax.generic(0.1, 'kg/h', 0), position: 'right', beginAtZero: true, suggestedMax: 7, afterDataLimits: sharedMax,
          grid: { drawOnChartArea: false }, afterFit: alignAxis },
  };
  const options = baseOptions(ctx, scales, [kg, kgh]);
  options.scales.x.offset = false;
  // mixed bar + line: point-style swatches (the line override applies to line charts only)
  options.plugins.legend.labels = { ...options.plugins.legend.labels, usePointStyle: true, boxWidth: 8, boxHeight: 8 };
  const points = [...barData, ...lineData].filter(d => d.y != null).length;
  return { type: 'bar', data: { datasets }, options, series, points };
}

/** The y axes in use (ticks in the theme's colours, units in the labels). */
export function axes(ctx: BuildCtx) {
  const { textColor, gridColor } = palette(ctx);
  const font = { size: fontSize(ctx) };
  const ticks = (callback: (v: number) => string, extra: any = {}) => ({
    color: textColor, font, maxTicksLimit: ctx.phone ? 5 : 6, callback: (v: any) => callback(Number(v)), ...extra,
  });
  const right = (callback: (v: number) => string, extra: any = {}) => ({
    position: 'right', grid: { drawOnChartArea: false }, ticks: ticks(callback, extra), afterFit: alignAxis,
  });
  return {
    // whole °C from the Fröling: at least 10 °C high, so a value flipping 45 ↔ 46 stays a calm line
    froelingTemp: { resolution: 1, grid: { color: gridColor }, ticks: ticks(v => `${fmtNum(v, 0)} °C`, { precision: 0 }) },
    roomTemp: (withSoll: boolean) => ({
      resolution: 0.1, ...(withSoll ? { grace: '5%' } : {}), grid: { color: gridColor },
      ticks: ticks(v => `${fmtNum(v, 1)} °`),
    }),
    watt: { resolution: 1, grid: { color: gridColor }, ticks: ticks(fmtW) },
    pct: { ...right(v => `${v} %`, { stepSize: 25 }), min: 0, max: 100 },
    pctLeft: { resolution: 1, min: 0, suggestedMax: 100, grid: { color: gridColor }, ticks: ticks(v => `${v} %`) },
    onOff: {
      ...right(v => v === 1 ? ctx.t('BOARD_ON') : v === 0 ? ctx.t('BOARD_OFF') : ''),
      min: -0.1, max: 1.1, afterBuildTicks: (s: any) => { s.ticks = [{ value: 0 }, { value: 1 }]; },
    },
    onOffLeft: {
      min: -0.05, max: 1.05, grid: { color: gridColor },
      afterBuildTicks: (s: any) => { s.ticks = [{ value: 0 }, { value: 1 }]; },
      ticks: ticks(v => v === 1 ? ctx.t('BOARD_ON') : v === 0 ? ctx.t('BOARD_OFF') : ''),
    },
    generic: (resolution: number, unit: string, digits: number) => ({
      resolution, grid: { color: gridColor },
      ticks: ticks(v => `${fmtNum(v, Math.max(0, digits))}${unit ? ' ' + unit : ''}`),
    }),
    /** An empty right axis for a chart without one: it takes the width of the board's right axes. */
    spacer: {
      position: 'right', display: true, grid: { display: false, drawTicks: false }, border: { display: false },
      ticks: { display: false }, afterFit: alignAxis,
    },
  };
}

function palette(ctx: BuildCtx): { textColor: string; gridColor: string } {
  return ctx.print ? { textColor: '#333', gridColor: 'rgba(0,0,0,0.12)' } : chartPalette(ctx.dark);
}

function fontSize(ctx: BuildCtx): number {
  return ctx.print ? 12 : ctx.phone ? 10 : 11;
}

/** Options shared by every board chart: the window as x axis, legend, tooltip, zoom → window, sync. */
export function baseOptions(ctx: BuildCtx, scales: Record<string, any>, fmts: ((v: number) => string)[]): any {
  const { textColor, gridColor } = palette(ctx);
  const font = { size: fontSize(ctx) };
  const win = ctx.win;
  // every chart: a left axis and a right one (an empty spacer if it has none), both of the board's widths
  if (scales['y']) {
    scales = { ...scales, y: { ...scales['y'], afterFit: alignAxis } };
    if (!scales['y1']) scales['y1'] = axes(ctx).spacer;
  }
  return {
    responsive: !ctx.print,
    maintainAspectRatio: false,
    animation: false,
    ...(ctx.print ? { devicePixelRatio: 2, events: [] } : {}),
    interaction: { mode: 'boardX', intersect: false },
    layout: { padding: { top: 4, right: 6, bottom: 0, left: 0 } },
    plugins: {
      legend: {
        display: true,
        labels: { color: textColor, font: { size: ctx.print ? 13 : ctx.phone ? 11 : 12 }, padding: ctx.phone ? 6 : 10 },
        // read-only (a snapshot link) and the PDF: a click changes nothing
        onClick: ctx.onLegend ? legendClick(ctx.onLegend) : () => {},
      },
      tooltip: {
        enabled: !ctx.print,
        position: 'boardTime',   // at the time shown (board-sync.ts)
        ...(ctx.phone ? { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 6, boxWidth: 8, boxHeight: 8 } : {}),
        callbacks: {
          title: (items: any[]) => { const ts = tooltipTime(items); return ts == null ? '' : fmtTime(ts, win); },
          label: (item: any) => {
            const y = item.parsed.y;
            if (y == null) return '';
            const f = fmts[item.datasetIndex];
            return ` ${item.dataset.label}: ${f ? f(y) : String(y)}`;
          },
        },
      },
      ...(ctx.onWindow ? { zoom: zoomOptions(ctx.onWindow, win) } : {}),
      crosshairLine: false,   // the board draws its own (synchronised) line
      boardSync: { group: ctx.group, axes: ctx.axes ?? null },
    },
    scales: {
      x: {
        type: 'linear',
        min: win.from,
        max: win.to,
        // whole hours / noons / month starts of the window, thinned by autoSkip
        afterBuildTicks: (scale: any) => {
          const t = alignedTimeTicks(scale.min, scale.max);
          if (t.length >= 2) scale.ticks = t.map(value => ({ value }));
        },
        ticks: {
          color: textColor, font, maxRotation: 0, autoSkip: true, autoSkipPadding: ctx.phone ? 10 : 16,
          callback: function (this: any, val: number | string) { return visibleTimeTick(this.min, this.max, val); },
        },
        grid: { color: gridColor },
      },
      ...scales,
    },
    elements: { line: { tension: 0.4 } },
  };
}

/** Drag (mouse) / pinch (touch) on a chart: the selected span becomes the board's window. Not the
 *  per-chart zoom link of chart.utils' dragZoom — the board window is the state. A call that leaves the
 *  axis on the chart's own window (`win`) is no gesture: chartjs-plugin-zoom's resetZoom (which the
 *  deep-link plugin runs on back / forward) reports one too. */
function zoomOptions(onWindow: (win: LinkWindow) => void, win: LinkWindow): any {
  return {
    zoom: {
      drag: { enabled: true, threshold: 8, backgroundColor: 'rgba(13,110,253,0.1)', borderColor: 'rgba(13,110,253,0.3)', borderWidth: 1 },
      pinch: { enabled: true },
      mode: 'x',
      onZoomComplete: ({ chart }: { chart: any }) => {
        const x = chart.scales?.x;
        if (!x || !isFinite(x.min) || !isFinite(x.max) || x.max <= x.min) return;
        if (Math.abs(x.min - win.from) < 1000 && Math.abs(x.max - win.to) < 1000) return;
        onWindow({ from: x.min, to: x.max });
      },
    },
  };
}

// ── min–max bands ────────────────────────────────────────────────────────────

/** The min–max band of `field` from the buckets' _min / _max (minmax=1; × scale), null when the answer
 *  carries none (raw points, or an older backend). */
export function bandOf(pts: readonly any[], field: string, scale = 1): BandPt[] | null {
  let any = false;
  const out = pts.map((p): BandPt => {
    const lo = p?._min?.[field], hi = p?._max?.[field];
    if (typeof lo !== 'number' || typeof hi !== 'number' || !isFinite(lo) || !isFinite(hi)) return { x: p.ts, lo: null, hi: null };
    any = true;
    return { x: p.ts, lo: Math.min(lo * scale, hi * scale), hi: Math.max(lo * scale, hi * scale) };
  });
  return any ? out : null;
}

/**
 * Chart.js plugin 'boardBands': a dataset's `band` (its buckets' min–max, see {@link bandOf}) as a faint
 * area in the series' colour under all lines, only while the series is visible (hidden with it). It is
 * no dataset: no legend entry, tooltip, crosshair value or stats. The series' y axis makes room for it
 * (not beyond bounds the axis fixes, e.g. 0–100 %).
 */
export const boardBandsPlugin: Plugin = {
  id: 'boardBands',

  afterDataLimits(chart, args: any) {
    const scale = args?.scale;
    if (!scale || scale.isHorizontal() || scale.type !== 'linear') return;
    const { minDefined, maxDefined } = scale.getUserBounds();
    if (minDefined && maxDefined) return;
    let lo = Infinity, hi = -Infinity;
    chart.data.datasets.forEach((ds: any, i) => {
      const band: BandPt[] | undefined = ds.band;
      const meta: any = chart.getDatasetMeta(i);
      if (!band?.length || !chart.isDatasetVisible(i) || (meta.yScale ?? meta.vScale) !== scale) return;
      for (const b of band) {
        if (b.lo != null && b.lo < lo) lo = b.lo;
        if (b.hi != null && b.hi > hi) hi = b.hi;
      }
    });
    if (!minDefined && isFinite(lo) && lo < scale.min) scale.min = lo;
    if (!maxDefined && isFinite(hi) && hi > scale.max) scale.max = hi;
  },

  beforeDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const banded = (ds: any, i: number) => !!ds.band?.length && chart.isDatasetVisible(i);
    // many bands on top of each other (the "all values" charts) get fainter, so the lines stay readable
    const fade = Math.min(1, 3 / Math.max(1, chart.data.datasets.filter(banded).length));
    chart.data.datasets.forEach((ds: any, i) => {
      const band: BandPt[] | undefined = ds.band;
      if (!band?.length || !banded(ds, i)) return;
      const meta: any = chart.getDatasetMeta(i);
      const xs = meta.xScale, ys = meta.yScale;
      if (!xs || !ys) return;
      ctx.save();
      ctx.beginPath();
      ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
      ctx.clip();
      ctx.globalAlpha = (typeof ds.bandAlpha === 'number' ? ds.bandAlpha : 0.15) * fade;
      ctx.fillStyle = ds.borderColor;
      // one closed area per run of buckets with a min / max: along the maxima, back along the minima
      let run: BandPt[] = [];
      const flush = () => {
        if (run.length > 1) {
          ctx.beginPath();
          run.forEach((b, k) => {
            const x = xs.getPixelForValue(b.x), y = ys.getPixelForValue(b.hi);
            if (k) ctx.lineTo(x, y); else ctx.moveTo(x, y);
          });
          for (let k = run.length - 1; k >= 0; k--) ctx.lineTo(xs.getPixelForValue(run[k].x), ys.getPixelForValue(run[k].lo));
          ctx.closePath();
          ctx.fill();
        }
        run = [];
      };
      for (const b of band) { if (b.lo == null || b.hi == null) flush(); else run.push(b); }
      flush();
      ctx.restore();
    });
  },
};

let bandsRegistered = false;
/** Register {@link boardBandsPlugin} (once). */
export function registerBoardBands(): void {
  if (bandsRegistered) return;
  bandsRegistered = true;
  Chart.register(boardBandsPlugin);
}

// ── stats ────────────────────────────────────────────────────────────────────

/** One number of a stats line ("max 78,0 °C"): the time of an extreme ("21.10. 07:42") and its tooltip. */
export interface StatPart { text: string; at?: string; title?: string; }

/** A series' summary over the window; approx: from bucket means (an older backend) or an extreme's time only known to
 *  its bucket (FieldStats.approx), shown with "≈". */
export interface StatResult { parts: StatPart[]; approx: boolean; }

/** "min 45,2 · Ø 62,1 · max 78,0 °C"; withTimes (the PDF): "min 45,2 (21.10. 03:12) · Ø 62,1 · max 78,0 °C (21.10. 07:42)". */
export function statLine(r: StatResult, withTimes = false): string {
  return (r.approx ? '≈ ' : '') + r.parts.map(p => withTimes && p.at ? `${p.text} (${p.at})` : p.text).join(' · ');
}

/** The time of an extreme, compact: "07:42" (windows up to ~a day), "21.10. 07:42", "21.10.25 07:42" (over a new year). */
export function fmtAt(ts: number, win: LinkWindow): string {
  const loc = uiLocale();
  const d = new Date(ts);
  const time = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
  if (win.to - win.from <= 20 * 3600_000) return time;
  const years = new Date(win.from).getFullYear() !== new Date(win.to).getFullYear();
  return `${d.toLocaleDateString(loc, years ? { day: '2-digit', month: '2-digit', year: '2-digit' } : { day: '2-digit', month: '2-digit' })} ${time}`;
}

/** The distinct /api/history/stats sources of a chart's series. */
export function statSourcesOfSeries(series: readonly BuiltSeries[]): StatSource[] {
  const out = new Map<string, StatSource>();
  for (const s of series) {
    const src = s.stat?.src;
    if (src) out.set(statSourceKey(src), { series: src.series, ...(src.agg ? { agg: src.agg } : {}) });
  }
  return [...out.values()];
}

/**
 * One series' summary over the window. A series with a stats source: from /api/history/stats (`exact`,
 * every raw point — true extremes with their times); where the backend has none (null) from `fallback`
 * (the same series at the fixed stats resolution; undefined: not loaded yet), "≈". A source not in
 * `exact` yet: nothing. Where the archive has no values but the chart has (the cistern's live history), or
 * a Soll of 0 is in the window, from the chart's own data, "≈". Without a source: from the chart's data.
 */
export function seriesStat(s: BuiltSeries, fallback: BuiltSeries | undefined, exact: ExactStats, win: LinkWindow, t: Tr,
                           now = Date.now()): StatResult | null {
  const st = s.stat;
  if (!st) return null;
  if (!st.src) return approxStat(fallback ?? s, win, t, now, !!st.approx);
  const key = statSourceKey(st.src);
  if (!exact.has(key)) return null;
  const hs = exact.get(key);
  if (!hs) return fallback ? approxStat(fallback, win, t, now, true) : null;
  const fs = hs.fields?.[st.src.field];
  const r = fs && fs.n > 0 ? exactStat(st, fs, !!hs.timeWeighted, win, t) : null;
  if (r) return r;
  const approx = approxStat(fallback ?? s, win, t, now, true);
  if (approx && fs && fs.n > 0 && st.src.positive && st.kind === 'mmm' && approx.parts.length === 3 && fs.max * (st.src.scale ?? 1) > 0) {
    // a Soll that was 0 (none) at times: min and Ø from the chart (its buckets without a 0), the true max from every point
    approx.parts[2] = extremePart(st, t('BOARD_STAT_MAX'), fs.max * (st.src.scale ?? 1), fs.maxTs, win, true);
  }
  return approx;
}

/** "max 78,0 °C" (unitAfter: the unit after the value), its time and a tooltip "max 78,0 °C · 21.10. 07:42". */
function extremePart(st: StatSpec, label: string, v: number, ts: number, win: LinkWindow, unitAfter: boolean): StatPart {
  const { f, unit } = statFormat(st);
  const at = typeof ts === 'number' && isFinite(ts) ? fmtAt(ts, win) : undefined;
  return { text: `${label} ${f(v)}${unitAfter ? unit : ''}`, at, title: `${label} ${f(v)}${unit}${at ? ' · ' + at : ''}` };
}

/** The summary from the endpoint's field stats (null: not usable — a sum, or a Soll with values ≤ 0). */
function exactStat(st: StatSpec, fs: FieldStats, timeWeighted: boolean, win: LinkWindow, t: Tr): StatResult | null {
  const src = st.src!;
  const nums = [fs.min, fs.max, fs.mean].every(v => typeof v === 'number' && isFinite(v));
  if (!nums || st.kind === 'sum') return null;
  const count = `${fmtNum(fs.n, 0)} ${t('BOARD_STAT_VALUES')}` + (timeWeighted ? ` · ${t('BOARD_STAT_TIME_WEIGHTED')}` : '');
  if (st.kind === 'on') {
    // 0 / 1 signals (pump state, a KNX bit) or 0 / 100 %: the mean is the share of the (time-weighted) samples "on"
    const level = Math.max(Math.abs(fs.min), Math.abs(fs.max)) > 1 ? 100 : 1;
    const share = Math.max(0, Math.min(1, fs.mean / level));
    return { parts: [{ text: t('BOARD_STAT_ON', { pct: Math.round(100 * share) }), title: count }], approx: false };
  }
  if (src.positive && fs.min <= 0) return null;
  const scale = src.scale ?? 1;
  let min = fs.min * scale, max = fs.max * scale, minTs = fs.minTs, maxTs = fs.maxTs;
  if (scale < 0) { [min, max] = [max, min]; [minTs, maxTs] = [maxTs, minTs]; }
  const mean = fs.mean * scale;
  const { f, unit } = statFormat(st);
  const parts: StatPart[] = [
    extremePart(st, t('BOARD_STAT_MIN'), min, minTs, win, false),
    { text: `${t('BOARD_STAT_AVG')} ${f(mean)}`, title: `${t('BOARD_STAT_AVG')} ${f(mean)}${unit} · ${count}` },
    extremePart(st, t('BOARD_STAT_MAX'), max, maxTs, win, true),
  ];
  if (st.kind === 'valve' && Array.isArray(fs.hist5) && fs.hist5.length === 20) {
    const pct = (v: number) => Math.round(100 * Math.max(0, Math.min(1, v || 0)));
    // ≥ 90 % open (bins 18–19) and < 5 % (bin 0): the same thresholds as the valves evaluation
    parts.push({ text: t('BOARD_STAT_VALVE_SHARES', { open: pct(fs.hist5[18] + fs.hist5[19]), closed: pct(fs.hist5[0]) }) });
  }
  // a Fröling read glitch left out, and the time of an extreme only known to its bucket
  return { parts, approx: !!fs.approx };
}

/** Number format of a stat: watts as W / kW per value, else fixed decimals with the unit once at the end. */
function statFormat(st: StatSpec): { f: (v: number) => string; unit: string } {
  return st.unit === 'W'
    ? { f: fmtW, unit: '' }
    : { f: (v: number) => fmtNum(v, st.digits), unit: st.unit ? ' ' + st.unit : '' };
}

/**
 * A series' summary from its chart data (bucket means where the window has more points than buckets).
 * Series that hold their values (StatSpec.hold, "on") are weighted by how long each value held within the window.
 */
function approxStat(s: BuiltSeries, win: LinkWindow, t: Tr, now: number, approx: boolean): StatResult | null {
  if (!s.stat) return null;
  const st = s.stat;
  const { f, unit } = statFormat(st);
  const mmm = (min: number, avg: number, max: number): StatResult => ({
    parts: [
      { text: `${t('BOARD_STAT_MIN')} ${f(min)}` },
      { text: `${t('BOARD_STAT_AVG')} ${f(avg)}` },
      { text: `${t('BOARD_STAT_MAX')} ${f(max)}${unit}` },
    ],
    approx,
  });
  if (st.kind === 'on' || (st.hold && st.kind !== 'sum')) {
    let min = Infinity, max = -Infinity, sum = 0, weight = 0;
    const end = Math.min(win.to, now);
    const d = s.data;
    for (let i = 0; i < d.length; i++) {
      const y = d[i].y;
      if (y == null || !isFinite(y)) continue;
      const a = Math.max(d[i].x, win.from);
      const b = Math.min(i + 1 < d.length ? d[i + 1].x : end, end);
      const inside = d[i].x >= win.from && d[i].x <= win.to;
      if (b <= a && !inside) continue;
      if (y < min) min = y;
      if (y > max) max = y;
      if (b > a) { sum += y * (b - a); weight += b - a; }
    }
    if (!isFinite(min)) return null;
    // 0 / 1 signals or 0 / 100 % (a circuit pump drawn as 100 %): the share of the time "on"
    const level = Math.max(Math.abs(min), Math.abs(max)) > 1 ? 100 : 1;
    if (st.kind === 'on') return weight > 0 ? { parts: [{ text: t('BOARD_STAT_ON', { pct: Math.round(100 * sum / weight / level) }) }], approx } : null;
    return mmm(min, weight > 0 ? sum / weight : (min + max) / 2, max);
  }
  const vals: number[] = [];
  for (const d of s.data) if (d.y != null && isFinite(d.y) && d.x >= win.from && d.x <= win.to) vals.push(d.y);
  if (!vals.length) return null;
  if (st.kind === 'sum') {
    return { parts: [{ text: `${t('BOARD_STAT_SUM')} ${fmtNum(vals.reduce((a, b) => a + b, 0), st.digits)}${st.unit ? ' ' + st.unit : ''}` }], approx };
  }
  let min = Infinity, max = -Infinity, sum = 0;
  for (const v of vals) { if (v < min) min = v; if (v > max) max = v; sum += v; }
  return mmm(min, sum / vals.length, max);
}
